import type { IconName } from "../components/ui/Icon";

export type Role = "admin" | "org_manager" | "customer" | string;

export interface NavItem {
  key: string;
  path: string;
  label: string;
  icon: IconName;
  description: string;
  /** 为空表示所有角色可见 */
  roles?: Role[];
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export const NAV_GROUPS: NavGroup[] = [
  {
    label: "运营",
    items: [
      { key: "tasks", path: "/tasks", label: "任务管理", icon: "tasks", description: "查看每次批量任务的执行状态、失败原因与算力消耗" },
      { key: "credits", path: "/token-records", label: "算力记录", icon: "zap", description: "充值、赠送与消耗逐笔入账，按账号追溯变动前后余额" },
    ],
  },
  {
    label: "资源",
    items: [
      { key: "users", path: "/users", label: "账号管理", icon: "users", roles: ["admin"], description: "管理成员、角色与邀请码，让每个运营动作都找到负责人" },
      { key: "orgs", path: "/orgs", label: "组织管理", icon: "org", roles: ["admin"], description: "维护组织信息与状态，账号通过邀请码归属到组织" },
      { key: "pricing", path: "/token-config", label: "算力配置", icon: "sliders", roles: ["admin"], description: "设置每类任务单次消耗的算力，三端统一生效" },
    ],
  },
  {
    label: "系统",
    items: [
      { key: "logins", path: "/login-records", label: "登录记录", icon: "loginRec", roles: ["admin"], description: "审计每一次登录的来源与设备，必要时让会话立即失效" },
      { key: "dict", path: "/dict", label: "字典管理", icon: "book", roles: ["admin"], description: "系统级键值配置，如新客赠送算力、活动白名单与最低版本" },
    ],
  },
];

export const ROLE_LABEL: Record<string, string> = {
  admin: "管理员",
  org_manager: "组织管理员",
  staff: "员工",
  customer: "客户",
};

export const canSee = (item: NavItem, roles: Role[]) => !item.roles || item.roles.some((role) => roles.includes(role));

export const visibleGroups = (roles: Role[]) =>
  NAV_GROUPS.map((group) => ({ ...group, items: group.items.filter((item) => canSee(item, roles)) })).filter((group) => group.items.length > 0);

export const findNav = (pathname: string) => {
  for (const group of NAV_GROUPS) {
    const item = group.items.find((entry) => entry.path === pathname);
    if (item) return { group, item };
  }
  return null;
};

/** 登录后按角色决定默认落点 */
export const homePathFor = (roles: Role[]) => (roles.includes("admin") ? "/users" : "/token-records");

/** 账号、算力与安装包统一由团队管理员办理，与《用户协议与隐私说明》口径一致 */
export const ADMIN_CONTACT_NOTE = "账号开通、算力充值与安装包，请联系为您开通账号的团队管理员。";
export const OFFICIAL_SITE = "https://www.ipin.work";
export const ICP_NUMBER = "粤ICP备2025481786号-1";
export const COMPANY_NAME = "深圳淘趣科技有限公司";
export const ICP_LINK = "https://beian.miit.gov.cn/#/Integrated/recordQuery";

export const CLIENT_VERSIONS = { desktop: "0.1.20", extension: "0.1.11" };

/**
 * 三端安装包地址。desktopWin 指向公开更新通道里对应版本的安装包（随 CLIENT_VERSIONS.desktop 变化）；
 * 留空的项，首页按钮会显示「向管理员获取」并提示联系团队管理员。
 * - desktopMac：暂无公开 macOS 安装包。
 * - extension：待上架 Chrome 应用商店后，填入商店链接。
 */
// 国内可达的下载镜像（阿里云 OSS，华北2）。用稳定别名，无需随版本改地址。
// 海外的 Cloudflare（download.ipin.work）仍作为自动更新与备份来源。
const CN_DOWNLOAD_BASE = "https://ipinwork.oss-cn-beijing.aliyuncs.com";
export const DOWNLOADS: { desktopWin?: string; desktopMac?: string; extension?: string } = {
  desktopWin: `${CN_DOWNLOAD_BASE}/desktop/stable/win/x64/mss-tools-latest-setup.exe`,
  desktopMac: undefined,
  extension: `${CN_DOWNLOAD_BASE}/extension/chrome/mss-plugin-latest.zip`,
};
